/**
 * VersionHistory — collapsed `Version history (n)` disclosure for one Phase 0 artifact.
 *
 * ── WHY THIS EXISTS (2026-10-03 P0 fix batch, Item 4 / §5.5, AD-10) ───────────────────────────
 * Item 4 resolves finding F-1 as option (a): editing a committed artifact starts a NEW VERSION, and
 * two surfaces say so in copy — `MarkdownLiveEditor`'s committed banner and `ResultRow`'s
 * `startsNewVersion` notice. Both promise that *"the committed version stays in the project's
 * history."* AD-10 is explicit that shipping that promise with nothing that SHOWS the history makes
 * it unverifiable, and an unverifiable reassurance about a sign-off record is worse than none.
 * This component is that surface.
 *
 * ── WHAT A "VERSION" IS ────────────────────────────────────────────────────────────────────────
 * One `artifact_commit_requests` row with `status = 'approved'`. No new table and no migration
 * (§5.3): each approved row already carries its own `commit_message`, `requested_by`,
 * `approved_by`, `github_commit_sha` and `resolved_at`, and the version's CONTENT is immutable in
 * GitHub at that SHA. `version_no` is a presentation ordinal computed by the server over
 * `resolved_at ASC`, never stored.
 *
 * ── RENDER RULES ───────────────────────────────────────────────────────────────────────────────
 *   • `n === 0` → renders NOTHING. An artifact that has never been committed has no history, and a
 *     "Version history (0)" row would be noise on the common case. §5.5: "renders only when n > 0".
 *   • The query is only enabled when the artifact could plausibly have history, so a never-
 *     committed artifact costs no request.
 *   • A query FAILURE also renders nothing. This is a supplementary disclosure beside a working
 *     editor; an error banner here would imply something is wrong with the document itself. The
 *     committed banner degrades independently (it falls back to `base_commit_sha`), so the user is
 *     never left with no information about the commit.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';

import { useArtifactVersions } from '@/hooks/useArtifacts';

export interface VersionHistoryProps {
  projectId: string;
  artifactType: string;
  /**
   * `projects.github_repo`, as `owner/repo`. When absent the SHA renders as plain text rather than
   * a link — a dead or guessed GitHub URL is worse than no link on an audit surface.
   */
  githubRepo?: string | null;
  /** Repo-relative path of the artifact file, for the blob permalink. */
  filePath?: string | null;
  /**
   * Whether to fetch at all. Pass `false` for an artifact that cannot have history yet (status
   * `generated`), so the five page-level artifact queries do not each drag a versions query along.
   */
  enabled?: boolean;
}

/** `2026-10-01` — short, unambiguous, locale-independent. */
function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 10);
}

export function VersionHistory({
  projectId,
  artifactType,
  githubRepo,
  filePath,
  enabled = true,
}: VersionHistoryProps): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const { data: versions } = useArtifactVersions(
    projectId,
    artifactType,
    enabled,
  );

  // §5.5 — nothing at all for an artifact with no approved commits. Also covers the pending and
  // errored query states, where `data` is undefined: no skeleton, no error row.
  if (!versions || versions.length === 0) return null;

  return (
    <div className="border-t border-outlines pt-3">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="flex items-center gap-1.5 text-sm font-medium text-text-secondary hover:text-text-primary"
      >
        {open ? (
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        )}
        {`Version history (${versions.length})`}
      </button>

      {open && (
        <ul className="mt-2 space-y-1.5" aria-label="Approved versions">
          {versions.map((version) => {
            const shortSha = version.github_commit_sha
              ? version.github_commit_sha.slice(0, 7)
              : null;
            // Only link when BOTH halves are known. A blob permalink needs the repo and the path;
            // guessing either produces a 404 on an audit trail.
            const permalink =
              githubRepo && filePath && version.github_commit_sha
                ? `https://github.com/${githubRepo}/blob/${version.github_commit_sha}/${filePath}`
                : null;

            return (
              <li
                key={version.id}
                className="flex flex-wrap items-baseline gap-x-2 text-sm text-text-secondary"
              >
                <span className="font-medium text-text-primary">
                  {`v${version.version_no}`}
                </span>
                <span>·</span>
                <span className="text-text-primary">
                  {version.commit_message}
                </span>
                <span>·</span>
                {/* `approved_by` is nullable on the row. "approved" without a name is honest;
                    substituting `requested_by` would attribute the sign-off to the submitter. */}
                <span>
                  {version.approved_by
                    ? `approved by ${version.approved_by}`
                    : 'approved'}
                </span>
                <span>·</span>
                <span>{formatDate(version.resolved_at)}</span>
                {shortSha &&
                  (permalink ? (
                    <a
                      href={permalink}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex items-center gap-1 font-mono text-xs text-primary hover:underline"
                    >
                      {shortSha}
                      <ExternalLink className="h-3 w-3" aria-hidden="true" />
                    </a>
                  ) : (
                    <span className="font-mono text-xs">{shortSha}</span>
                  ))}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default VersionHistory;
